import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  MessageCircle, ArrowDown, Home as HomeIcon, Building2, Truck, Check,
  Star, ChevronDown, ArrowRight,
} from "lucide-react";
import { useLang } from "../i18n";
import { IMG, HOME_SERVICES, HOME_EXTRAS, PRICING, ZONES, waQuote } from "../data/site";
import Seo, { ORG_LD } from "../components/Seo";
import { MaskLine, Reveal, Kicker } from "../components/Reveal";
import ServiceCard from "../components/ServiceCard";
import ContactForm from "../components/ContactForm";
import Reviews from "../components/Reviews";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const MARQUEE_ITEMS = ["DETAILING", "PULIDO", "CERÁMICA", "FUSSO COAT", "INTERIOR", "EXTERIOR", "FAROS", "CORRECCIÓN"];

export default function HomePage() {
  const { lang, t } = useLang();
  const prefix = lang === "ca" ? "/ca" : "";
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <div data-testid="home-page">
      <Seo title={t("seo.home.title")} desc={t("seo.home.desc")} path="/" jsonLd={ORG_LD} image={IMG.hero} />

      {/* HERO */}
      <section ref={heroRef} className="spotlight relative flex min-h-[100svh] flex-col justify-end overflow-hidden bg-[#050505]">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <img
            src={IMG.hero}
            alt={lang === "ca" ? "Cotxe esportiu negre detallat en estudi fosc" : "Coche deportivo negro detallado en estudio oscuro"}
            className="h-[115%] w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/55 to-[#050505]/20" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505]/80 via-transparent to-transparent" />
        </motion.div>

        <motion.div style={{ y: titleY, opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-28 pt-40 sm:px-6 lg:px-12">
          <Reveal delay={0.1} y={16}>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#8BFF32]">{t("hero.kicker")}</p>
          </Reveal>
          <h1 className="mt-6 max-w-4xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white sm:text-5xl lg:text-7xl">
            <MaskLine delay={0.25}>{t("hero.h1a")}</MaskLine>
            <MaskLine delay={0.45} className="text-glow text-[#8BFF32]">{t("hero.h1b")}</MaskLine>
          </h1>
          <Reveal delay={0.7}>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg">{t("hero.sub")}</p>
          </Reveal>
          <Reveal delay={0.85}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a
                href={waQuote(lang === "ca" ? "Hola EcoGlow! Vull demanar pressupost." : "¡Hola EcoGlow! Quiero pedir presupuesto.")}
                target="_blank" rel="noopener noreferrer"
                data-testid="hero-quote-button"
                className="flex items-center gap-2 rounded-full bg-[#63E51D] px-7 py-4 font-display text-lg font-bold uppercase tracking-wide text-black transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_30px_rgba(99,229,29,0.5)] active:scale-[0.98]"
              >
                <MessageCircle className="h-5 w-5" /> {t("hero.cta1")}
              </a>
              <Link
                to={`${prefix}/servicios`}
                data-testid="hero-services-button"
                className="flex items-center gap-2 rounded-full border border-white/25 px-7 py-4 font-display text-lg font-bold uppercase tracking-wide text-white backdrop-blur-sm transition-all duration-300 hover:border-[#63E51D] hover:text-[#8BFF32]"
              >
                {t("hero.cta2")} <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </Reveal>
        </motion.div>

        <motion.div style={{ opacity: fade }} className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2">
          <div className="flex flex-col items-center gap-2 text-zinc-500">
            <span className="font-mono2 text-[10px] uppercase tracking-[0.3em]">{t("hero.scroll")}</span>
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
              <ArrowDown className="h-4 w-4" />
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <div className="overflow-hidden border-y border-white/5 bg-[#080808] py-5" data-testid="editorial-marquee">
        <div className="animate-marquee flex w-max items-center gap-10">
          {[...Array(2)].map((_, dup) =>
            MARQUEE_ITEMS.map((item, i) => (
              <span key={`${dup}-${i}`} className="flex items-center gap-10">
                <span className="font-display text-3xl font-bold uppercase tracking-widest text-white/10">{item}</span>
                <Star className="h-4 w-4 fill-[#63E51D] text-[#63E51D]" />
              </span>
            ))
          )}
        </div>
      </div>

      {/* INTRO */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32">
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
          <div>
            <Reveal><Kicker>{t("intro.kicker")}</Kicker></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl lg:text-5xl lg:leading-[1.05]">
                {t("intro.title")}
              </h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-lg text-base leading-relaxed text-zinc-400">{t("intro.text")}</p>
            </Reveal>
          </div>
          <div className="flex flex-col justify-center gap-4">
            {[
              { icon: HomeIcon, t: t("intro.m1t"), d: t("intro.m1d") },
              { icon: Building2, t: t("intro.m2t"), d: t("intro.m2d") },
              { icon: Truck, t: t("intro.m3t"), d: t("intro.m3d") },
            ].map((m, i) => (
              <Reveal key={i} delay={0.1 + i * 0.1}>
                <div className="flex items-center gap-5 rounded-xl border border-white/5 bg-[#121212] p-6 transition-all duration-300 hover:border-[#63E51D]/40 hover:shadow-[0_0_20px_rgba(99,229,29,0.1)]">
                  <span className="rounded-full bg-[#63E51D]/10 p-3.5 text-[#63E51D]"><m.icon className="h-6 w-6" /></span>
                  <div>
                    <h3 className="font-display text-xl font-bold uppercase tracking-wide text-white">{m.t}</h3>
                    <p className="text-sm text-zinc-400">{m.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="bg-[#080808] py-24 lg:py-32" data-testid="home-services-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
            <div>
              <Reveal><Kicker>{t("home.servicesKicker")}</Kicker></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl lg:text-5xl">
                  {t("home.servicesTitle")}
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <p className="max-w-md text-sm leading-relaxed text-zinc-400">{t("home.servicesSub")}</p>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {HOME_SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                <ServiceCard service={s} index={i} />
              </Reveal>
            ))}
            {HOME_EXTRAS.map((ex, i) => (
              <Reveal key={ex.key} delay={(i % 3) * 0.08}>
                <Link
                  to={`${prefix}/servicios/servicios-extra`}
                  data-testid={`service-card-extra-${ex.key}`}
                  className="card-sheen group relative flex flex-col overflow-hidden rounded-xl border border-white/5 bg-[#121212] transition-all duration-300 hover:-translate-y-1 hover:border-[#63E51D]/50 hover:shadow-[0_0_25px_rgba(99,229,29,0.15)]"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                      src={ex.img}
                      alt={ex[lang]?.name || ex.es.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#121212] via-transparent to-transparent" />
                    <span data-testid={`extra-price-${ex.key}`} className="absolute right-4 top-4 rounded-full bg-[#63E51D] px-3.5 py-1.5 font-display text-base font-bold text-black">
                      {ex.price}
                    </span>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-white group-hover:text-[#8BFF32]">
                      {ex[lang]?.name || ex.es.name}
                    </h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-zinc-400">{ex[lang]?.short || ex.es.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#63E51D]">
                      {t("home.viewService")}
                      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PACKS TEASER */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32" data-testid="home-packs-section">
        <div className="mb-14 text-center">
          <Reveal><Kicker>{t("home.packsKicker")}</Kicker></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl lg:text-5xl">
              {t("home.packsTitle")}
            </h2>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:items-stretch">
          {PRICING.packs.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1} className="h-full">
              <div
                data-testid={`pack-card-${p.id}`}
                className={`card-sheen relative flex h-full flex-col rounded-2xl border p-8 transition-all duration-300 hover:-translate-y-1 ${
                  p.recommended
                    ? "lime-glow border-[#63E51D] bg-[#101508]"
                    : "border-white/8 bg-[#121212] hover:border-[#63E51D]/40"
                }`}
              >
                {p.recommended && (
                  <span data-testid="pack-recommended-badge" className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-full bg-[#63E51D] px-4 py-1 font-mono2 text-[11px] font-bold uppercase tracking-widest text-black">
                    {t("home.recommended")}
                  </span>
                )}
                <h3 className="font-display text-2xl font-bold uppercase tracking-wide text-white">
                  {t(`packs.packNames.${p.id}`)}
                </h3>
                <p className="mt-4 font-display text-5xl font-black text-[#8BFF32]">
                  {p.prices[0]} €<span className="ml-2 font-mono2 text-sm font-normal text-zinc-500">S</span>
                </p>
                <p className="mt-1 text-xs text-zinc-500">{t("packs.sizeNote")}</p>
                <ul className="mt-6 flex-1 space-y-2.5">
                  {t(`packs.packItems.${p.id}`).map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-sm text-zinc-300">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#63E51D]" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-12 text-center">
            <Link
              to={`${prefix}/packs`}
              data-testid="home-packs-cta-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#63E51D] px-8 py-4 font-display text-lg font-bold uppercase tracking-wide text-black transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_25px_rgba(99,229,29,0.4)]"
            >
              {t("home.packsCta")} <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* WORKS */}
      <section className="bg-[#080808] py-24 lg:py-32" data-testid="home-works-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <Reveal>
              <div className="relative overflow-hidden rounded-2xl border border-white/5">
                <img src={IMG.correction} alt={lang === "ca" ? "Procés de correcció de pintura" : "Proceso de corrección de pintura"} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/85 to-transparent" />
                <p className="absolute bottom-4 left-4 max-w-xs font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                  {t("home.worksNote")}
                </p>
              </div>
            </Reveal>
            <div>
              <Reveal><Kicker>{t("home.worksKicker")}</Kicker></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl lg:text-5xl">
                  {t("home.worksTitle")}
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 text-base leading-relaxed text-zinc-400">{t("home.worksPlaceholder")}</p>
              </Reveal>
              <Reveal delay={0.3}>
                <Link to={`${prefix}/trabajos-realizados`} data-testid="home-works-cta-link" className="mt-8 inline-flex items-center gap-2 font-semibold text-[#63E51D] hover:text-[#8BFF32]">
                  {t("home.worksCta")} <ArrowRight className="h-4 w-4" />
                </Link>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32">
        <div className="mb-14">
          <Reveal><Kicker>{t("home.whyKicker")}</Kicker></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl lg:text-5xl">
              {t("home.whyTitle")}
            </h2>
          </Reveal>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {t("home.why").map((w, i) => (
            <Reveal key={i} delay={(i % 3) * 0.08} className={i === 0 ? "sm:col-span-2 lg:col-span-1" : ""}>
              <div className="card-sheen h-full rounded-xl border border-white/5 bg-[#121212] p-7 transition-all duration-300 hover:border-[#63E51D]/40">
                <span className="font-mono2 text-xs font-bold text-[#63E51D]">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-display text-xl font-bold uppercase tracking-wide text-white">{w.t}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ZONES */}
      <section className="bg-[#080808] py-24 lg:py-32" data-testid="home-zones-section">
        <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-12">
          <Reveal><Kicker>{t("home.zonesKicker")}</Kicker></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl lg:text-5xl">
              {t("home.zonesTitle")}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mx-auto mt-4 max-w-xl text-sm text-zinc-400">{t("home.zonesSub")}</p>
          </Reveal>
          <div className="mt-12 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {ZONES.map((z, i) => (
              <Reveal key={z} delay={i * 0.06}>
                <Link to={`${prefix}/zonas-de-servicio`} data-testid={`zone-chip-${i}`} className="block rounded-full border border-white/10 bg-[#121212] px-4 py-3.5 font-display text-lg font-bold uppercase tracking-wide text-zinc-200 transition-all duration-300 hover:border-[#63E51D]/60 hover:text-[#8BFF32]">
                  {z}
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <Reviews />

      {/* FAQ */}
      <section className="bg-[#080808] py-24 lg:py-32" data-testid="home-faq-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-12">
          <div className="mb-12 text-center">
            <Reveal><Kicker>{t("home.faqKicker")}</Kicker></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl lg:text-5xl">
                {t("home.faqTitle")}
              </h2>
            </Reveal>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {t("faqs").map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i}`} className="rounded-xl border border-white/5 bg-[#121212] px-6">
                <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-5 text-left font-display text-lg font-bold uppercase tracking-wide text-white hover:text-[#8BFF32] hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-zinc-400">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CONTACT */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-12 lg:py-32" data-testid="home-contact-section">
        <div className="mb-14">
          <Reveal><Kicker>{t("home.contactKicker")}</Kicker></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl lg:text-5xl">
              {t("home.contactTitle")}
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-4 max-w-lg text-sm text-zinc-400">{t("home.contactSub")}</p>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <ContactForm />
        </Reveal>
      </section>
    </div>
  );
}
